<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import { apiErrorMessage } from '../../../../api/coLiving/errors';
import { formatTimestamp } from '../ticketHelpers';
import {
  PHOTO_ACCEPT,
  DEFAULT_MAX_IMAGES,
  DEFAULT_MAX_IMAGE_MB,
  photoProblem,
  photoRoom,
  photoNote,
} from '../photos';

// The photos of one ticket: those the tenant sent in the chat (copied here by
// a background job) and those an administrator uploads. Everyone who can open
// the ticket sees them; only administrators add or delete.
export default {
  name: 'CoLivingTicketPhotos',
  props: {
    ticket: { type: Object, required: true },
    canEdit: { type: Boolean, default: false },
    // From the settings; the server enforces them too.
    maxImages: { type: Number, default: DEFAULT_MAX_IMAGES },
    maxImageMb: { type: Number, default: DEFAULT_MAX_IMAGE_MB },
    timezone: { type: String, default: '' },
  },
  emits: ['refresh'],
  data() {
    return {
      accept: PHOTO_ACCEPT,
      isUploading: false,
      deletingId: '',
      // Photo links last an hour: one reload per ticket when an image fails.
      reloadedFor: '',
    };
  },
  computed: {
    media() {
      return this.ticket.media || [];
    },
    room() {
      return photoRoom(this.media, this.maxImages);
    },
  },
  methods: {
    ...mapActions('coLivingTickets', ['addPhotos', 'removePhoto']),
    formatTimestamp,
    note(item) {
      const code = photoNote(item);
      return code ? this.$t(`CO_LIVING.TICKETS.PHOTOS.NOTES.${code}`) : '';
    },
    async onPick(event) {
      const input = event.target;
      const files = Array.from(input.files || []);
      input.value = '';
      if (!files.length) return;
      const bad = files.find(file => photoProblem(file, this.maxImageMb));
      if (bad) {
        useAlert(
          this.$t(
            `CO_LIVING.TICKETS.PHOTOS.PROBLEMS.${photoProblem(
              bad,
              this.maxImageMb
            )}`,
            { name: bad.name, mb: this.maxImageMb }
          )
        );
        return;
      }
      if (files.length > this.room) {
        useAlert(
          this.$t('CO_LIVING.TICKETS.PHOTOS.PROBLEMS.LIMIT', {
            max: this.maxImages,
          })
        );
        return;
      }
      this.isUploading = true;
      try {
        await this.addPhotos({ id: this.ticket.id, files });
        useAlert(this.$t('CO_LIVING.TICKETS.PHOTOS.ADDED'));
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'CO_LIVING.TICKETS.PHOTOS.ADD_FAILED')
        );
      } finally {
        this.isUploading = false;
      }
    },
    async onDelete(item) {
      this.deletingId = item.id;
      try {
        await this.removePhoto({ id: this.ticket.id, mediaId: item.id });
        useAlert(this.$t('CO_LIVING.TICKETS.PHOTOS.DELETED'));
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'CO_LIVING.TICKETS.PHOTOS.DELETE_FAILED')
        );
      } finally {
        this.deletingId = '';
      }
    },
    onImageError() {
      if (this.reloadedFor === this.ticket.id) return;
      this.reloadedFor = this.ticket.id;
      this.$emit('refresh');
    },
    openChat() {
      this.$router.push({
        name: 'inbox_conversation',
        params: { conversation_id: this.ticket.conversation_id },
      });
    },
  },
};
</script>

<template>
  <div data-testid="co-living-photos">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
        {{ $t('CO_LIVING.TICKETS.PHOTOS.TITLE') }}
      </h3>
      <label
        v-if="canEdit && room > 0"
        class="relative inline-flex items-center px-3 py-1.5 text-sm font-medium rounded cursor-pointer bg-slate-700 text-white hover:bg-slate-600 focus-within:ring-2 focus-within:ring-woot-500 transition-colors"
        :class="{ 'opacity-60 pointer-events-none': isUploading }"
        data-testid="co-living-photos-add"
      >
        {{
          isUploading
            ? $t('CO_LIVING.TICKETS.PHOTOS.UPLOADING')
            : $t('CO_LIVING.TICKETS.PHOTOS.ADD')
        }}
        <input
          type="file"
          multiple
          class="sr-only"
          :accept="accept"
          :disabled="isUploading"
          data-testid="co-living-photos-input"
          @change="onPick"
        />
      </label>
    </div>

    <p
      v-if="!media.length"
      class="mt-2 text-sm text-slate-500 dark:text-slate-400"
      data-testid="co-living-photos-empty"
    >
      {{ $t('CO_LIVING.TICKETS.PHOTOS.EMPTY') }}
    </p>
    <ul
      v-else
      class="grid grid-cols-2 gap-3 mt-3 list-none sm:grid-cols-3 md:grid-cols-4"
    >
      <li
        v-for="item in media"
        :key="item.id"
        class="flex flex-col gap-1 text-xs"
        :data-testid="`co-living-photo-${item.id}`"
      >
        <a
          v-if="item.status === 'stored' && item.url"
          :href="item.url"
          target="_blank"
          rel="noopener noreferrer"
          class="block overflow-hidden border rounded-lg border-slate-200 dark:border-slate-700 hover:ring-2 hover:ring-woot-500 transition"
        >
          <img
            :src="item.url"
            :alt="item.file_name || ''"
            class="object-cover w-full h-28"
            loading="lazy"
            :data-testid="`co-living-photo-img-${item.id}`"
            @error="onImageError"
          />
        </a>
        <div
          v-else
          class="flex items-center justify-center h-28 p-2 text-center border border-dashed rounded-lg border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400"
          :data-testid="`co-living-photo-note-${item.id}`"
        >
          {{ note(item) }}
        </div>
        <button
          v-if="
            item.error === 'attachment_unreachable' && ticket.conversation_id
          "
          type="button"
          class="text-left text-woot-600 hover:underline dark:text-woot-400"
          :data-testid="`co-living-photo-chat-${item.id}`"
          @click="openChat"
        >
          {{ $t('CO_LIVING.TICKETS.PHOTOS.OPEN_CHAT') }}
        </button>
        <div class="flex items-center justify-between gap-2">
          <span class="truncate text-slate-500">
            {{
              item.source === 'chat'
                ? $t('CO_LIVING.TICKETS.PHOTOS.FROM_CHAT')
                : $t('CO_LIVING.TICKETS.PHOTOS.FROM_PORTAL')
            }}
            · {{ formatTimestamp(item.created_at, timezone) }}
          </span>
          <woot-button
            v-if="canEdit"
            v-tooltip.top="$t('CO_LIVING.COMMON.DELETE')"
            variant="clear"
            size="small"
            color-scheme="alert"
            icon="delete"
            :aria-label="$t('CO_LIVING.COMMON.DELETE')"
            :is-loading="deletingId === item.id"
            :data-testid="`co-living-photo-delete-${item.id}`"
            @click="onDelete(item)"
          />
        </div>
      </li>
    </ul>
    <p
      v-if="canEdit && media.length"
      class="mt-2 text-xs text-slate-500 dark:text-slate-400"
    >
      {{
        $t('CO_LIVING.TICKETS.PHOTOS.LIMITS', {
          max: maxImages,
          mb: maxImageMb,
        })
      }}
    </p>
  </div>
</template>
